import {useContext, type ComponentProps} from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import {cn} from "../lib/utils";
import {TooltipContext, TooltipProvider} from "./context/TooltipContext";

interface TooltipProps extends ComponentProps<typeof TooltipPrimitive.Root> {
	disabled?: boolean;
}

function Tooltip({disabled = false, children, ...props}: TooltipProps) {
	return (
		<TooltipContext.Provider value={{disabled}}>
			<TooltipProvider disableHoverableContent>
				<TooltipPrimitive.Root data-slot="tooltip" {...props}>
					{children}
				</TooltipPrimitive.Root>
			</TooltipProvider>
		</TooltipContext.Provider>
	);
}

function TooltipTrigger({children, ...props}: ComponentProps<typeof TooltipPrimitive.Trigger>) {
	const {disabled} = useContext(TooltipContext);
	if (disabled) return <>{children}</>;

	return (
		<TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props}>
			{children}
		</TooltipPrimitive.Trigger>
	);
}

const contentStyles = "v3 z-50 w-fit max-w-xs rounded-md bg-foreground-100 px-3 py-1.5 text-xs text-background-1000";
const arrowStyles = "z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-foreground-100 fill-foreground-100";

function TooltipContent({
	className,
	sideOffset = 0,
	children,
	...props
}: ComponentProps<typeof TooltipPrimitive.Content>) {
	const {disabled} = useContext(TooltipContext);
	if (disabled) return null;

	return (
		<TooltipPrimitive.Portal>
			<TooltipPrimitive.Content data-slot="tooltip-content" sideOffset={sideOffset} className={cn(contentStyles, className)} {...props}>
				{children}
				<TooltipPrimitive.Arrow className={arrowStyles}/>
			</TooltipPrimitive.Content>
		</TooltipPrimitive.Portal>
	);
}

export {Tooltip, TooltipTrigger, TooltipContent};
